/**
 * The four policy-posture figures, shared by Policies -> Overview and the Dashboard.
 *
 * PRESENTATIONAL ON PURPOSE. The Policies page already holds the ledger, the coverage and the
 * settings for its own cards, so making this component fetch would duplicate three requests on
 * that page. It takes numbers; each caller brings its own.
 *
 * THREE EVENT COUNTS AND ONE INVENTORY. "blocked now" is the odd one out - it is what a policy
 * would refuse at this instant, not something that happened - which is why its label says so and
 * its tooltip spells out the difference. Without that it reads as a third 30-day count.
 *
 * LINKED ON THE DASHBOARD, PLAIN ON THE POLICIES PAGE. `policiesPath` turns each tile into a link
 * into the view that explains it. On the Policies page itself there is nowhere to go - the ledger
 * is one tab away and the tiles describe the page you are on - so it is omitted there.
 */
import { Link } from 'react-router-dom'
import { Tooltip } from '@/shared/components/Tooltip'
import { blockedNow } from '@/features/policies/lib/decisionView'
import { formatCount } from '@/lib/format'
import type { PolicySettings } from '@/lib/apiTypes'

export interface PolicyKpiStripProps {
  /** Refusals in the window, from the decision ledger's verdict facet. */
  blocks: number
  /** Human overrides in the window, from the same facet. */
  overrides: number
  /** Settings payload - the source of the three "blocked now" inventory counts. */
  settings: PolicySettings | null
  /** How many authored policies have ever refused anything, out of how many exist. */
  fired: { fired: number; total: number }
  windowDays: number
  /**
   * Base path of the org's Policies page (e.g. `/org/acme/policies`). Given, every tile becomes a
   * link into the view that explains it: the two event counts open the ledger already filtered to
   * that verdict, the inventory count opens the tab whose tiles break it down per artefact.
   */
  policiesPath?: string
}

export function PolicyKpiStrip({
  blocks, overrides, settings, fired, windowDays, policiesPath,
}: PolicyKpiStripProps) {
  const now = blockedNow(settings)
  const href = (suffix: string) => (policiesPath ? `${policiesPath}${suffix}` : undefined)

  return (
    <div className="stat-strip fu">
      <Tile
        label={`refusals · ${windowDays}d`}
        value={formatCount(blocks)}
        color={blocks > 0 ? 'var(--red)' : undefined}
        href={href('?tab=decisions&verdict=block')}
      />
      <Tile
        label={`overrides · ${windowDays}d`}
        value={formatCount(overrides)}
        color={overrides > 0 ? 'var(--amber)' : undefined}
        href={href('?tab=decisions&verdict=override')}
      />
      {/* The one INVENTORY figure in a strip of event counts, which is why the label says so and
          the tooltip spells out the difference - otherwise it reads as a third 30-day count. */}
      <Tile
        label="blocked now · artefacts"
        value={formatCount(now.total)}
        color={now.total > 0 ? 'var(--red)' : undefined}
        href={href('')}
        hint={
          <span>
            Artefacts a policy would refuse <strong>right now</strong> — not refusals that happened.
            {now.parts.length > 0 && (
              <>
                {' '}
                {now.parts.map((p) => `${p.n} ${p.label}`).join(', ')}.
              </>
            )}
          </span>
        }
      />
      <Tile
        label="policies that fired"
        value={`${formatCount(fired.fired)} of ${formatCount(fired.total)}`}
        href={href('?tab=decisions')}
      />
    </div>
  )
}

function Tile({ label, value, color, hint, href }: {
  label: string
  value: string
  color?: string
  hint?: React.ReactNode
  href?: string
}) {
  const body = (
    <>
      <div className="stat-num" style={color ? { color } : undefined}>{value}</div>
      <div className="stat-label">{label}</div>
    </>
  )
  // The link IS the cell rather than a wrapper around it: `.stat-cell` carries `flex: 1`, which
  // only applies while the cell is the strip's own flex child.
  const cell = href
    ? <Link to={href} className="stat-cell" style={{ color: 'inherit', textDecoration: 'none' }}>{body}</Link>
    : <div className="stat-cell">{body}</div>
  // The tooltip wraps the whole cell, so the hint is reachable without adding a tab stop inside it.
  return hint ? <Tooltip text={hint} placement="bottom" interactive>{cell}</Tooltip> : cell
}
